import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { calculateBarWidthPct } from "src/utils/chartBarScale";

export type MetaFunnelStep = {
  label: string;
  value: number;
  color: string;
  kind: "currency" | "count";
};

interface ConversionFunnelPanelProps {
  title: string;
  tooltipInfo: DefinitionTooltipInfo;
  steps: MetaFunnelStep[];
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  numberLocale: string;
}

const ConversionFunnelPanel: FC<ConversionFunnelPanelProps> = ({
  title,
  tooltipInfo,
  steps,
  formatMoney,
  numberLocale,
}) => {
  const { t } = useTranslation();
  const funnelValues = steps.map(item => item.value);
  const maxVal = Math.max(...funnelValues, 1);
  const minVal = Math.min(...funnelValues.filter(v => v > 0), 1);

  return (
    <article className="paid-media-panel">
      <div className="paid-media-panel__header">
        <div className="paid-media-panel__heading">
          <h3>{title}</h3>
          <DefinitionTooltip
            label={title}
            info={tooltipInfo}
            align="end"
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
      </div>
      <div className="paid-media-funnel">
        {steps.map(step => (
          <div key={step.label} className="paid-media-funnel__step">
            <div className="paid-media-funnel__meta">
              <span>{step.label}</span>
              <strong>
                {step.kind === "currency"
                  ? formatMoney(step.value, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  : step.value.toLocaleString(numberLocale)}
              </strong>
            </div>
            <div className="paid-media-funnel__track">
              <div
                className="paid-media-funnel__fill"
                style={{
                  width: `${calculateBarWidthPct(step.value, maxVal, minVal, { mode: "log" })}%`,
                  background: step.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </article>
  );
};

export default ConversionFunnelPanel;
